<template>
  <div :class="[$style.reports]">
    <reports-title
      :title="'reports_access_title'"
      icon-color="#00564C"
      icon-name="report"
      :has-tooltip="false"
    />
    <pricing-stub
      v-if="!pricingAccessToReports"
      :is-owner="isOwner"
      :plan-title="planTitle"
    />
    <div
      v-else
      :class="[$style.example_box]"
    >
      <div :class="$style.example_content_top">
        <div :class="$style.example_content_tabs">
          <vgp-tabs
            :tabs="sectionTabs"
            :active="activeTab"
            :active-by="'id'"
            :blocked-inner-selection="true"
            type="button"
            @onToggle="toggleTab"
          />
        </div>

        <div :class="$style.feedback_button">
          <div
            :class="$style.text_button"
            @click="onClickFeedback"
          >
            {{ locale("header_contact_btn") }}
          </div>
        </div>
      </div>

      <div
        ref="scrollContainer"
        :class="[$style.example_content_charts, 'gantt_scroll']"
      >
        <div :class="$style.example_content_container">
          <div
            id="sec_0"
            :style="{height: '26px'}"
          />
          <div
            id="sec_1"
            ref="progressSec"
            :class="$style.example_content_item"
          >
            <span
              :id="'span_'+1"
              v-intersect="handleIntersect"
            />
            <progress-example
              :title="locale('report_tab_progress_and_performancel')"
              @onCreateReport="createReport"
            />
          </div>
          <div
            id="sec_2"
            ref="budgetSec"
            :class="$style.example_content_item"
          >
            <span
              :id="'span_'+2"
              v-intersect="handleIntersect"
            />
            <budget-example
              :title="locale('report_tab_budget')"
              @onCreateReport="createReport"
            />
          </div>
          <div
            id="sec_3"
            :class="$style.example_content_item"
          >
            <span
              :id="'span_'+3"
              v-intersect="handleIntersect"
            />
            <time-example
              :title="locale('report_tab_time')"
              @onCreateReport="createReport"
            />
          </div>
        </div>
      </div>
    </div>

    <reports-form
      v-if="isShowForm"
      :title="reportTitle"
      :report-type="routerName"
      :show-form="isShowForm"
      :chart-type="chartType"
      :router-name="routerName"
      @onToggle="createReport"
    />
  </div>
</template>

<script>
import reportsTitle from './include/title.vue';
import progressExample from './include/examples/progressExample.vue';
import budgetExample from './include/examples/budgetExample.vue';
import timeExample from './include/examples/timeExample.vue';
import routerHelper from '../../helpers/router';
import pricingHelper from '../../helpers/pricingHelper';
import rights from '../../components/rights/index';
import pricingStub from './include/pricingStub/stub.vue';
import reportsForm from './include/reportsForm.vue';
import reportHelpers from './include/helpers';

export default {
  components: {
    reportsTitle,
    progressExample,
    budgetExample,
    timeExample,
    pricingStub,
    reportsForm,
  },
  data() {
    return {
      locale: __,
      chartType: null,
      chartMode: 'graphic',
      routerName: '',
      tab_selected: 0,
      activeTab: {
        id: 0,
        title: __('report_tab_all'),
        name: 'All',
      },
      sectionTabs: [
        {
          id: 0,
          title: __('report_tab_all'),
          name: 'All',
        },
        {
          id: 1,
          title: __('report_tab_progress_and_performancel'),
          name: 'Progress',
        },
        {
          id: 2,
          title: __('report_tab_budget'),
          name: 'Budget',
        },
        {
          id: 3,
          title: __('report_tab_time'),
          name: 'Time on tasks',
        },
      ],
      reportTitle: '',
      isShowForm: false,
    };
  },
  computed: {
    pricingAccessToReports() {
      return pricingHelper.checkPricingAccess('reports');
    },
    planTitle() {
      return `${pricingHelper.getPlans('reports')}`;
    },
    isOwner() {
      return rights.account.isOwner();
    },
  },

  mounted() {
    this.$nextTick(() => {
      userExtAnalytics.log('reports_all_open');

      if (!this.pricingAccessToReports) return;

      const scrollContainer = this.$refs.scrollContainer;
      const currentRoute = this.$store.getters['headerView/currentRoute'];
      const prevRoutePath = currentRoute?.prevRoute?.path;

      this.activeTab = this.sectionTabs[0];

      if (prevRoutePath && (prevRoutePath.includes('/reports/budget') || prevRoutePath.includes('/reports/costslist'))) {
        this.toggleTab(this.sectionTabs[2], false);
      } else if (prevRoutePath && (prevRoutePath.includes('/reports/time') || prevRoutePath.includes('/reports/timeloglist'))) {
        this.toggleTab(this.sectionTabs[3], false);
      } else {
        this.toggleTab(this.sectionTabs[0], false);
      }
      scrollContainer.addEventListener('scroll', this.scrollListener);
    });
  },

  beforeUnmount() {
    if (!this.pricingAccessToReports) return;

    const scrollContainer = this.$refs.scrollContainer;

    scrollContainer.removeEventListener('scroll', this.scrollListener);
  },

  methods: {
    scrollListener() {
      const scrollContainer = this.$refs.scrollContainer;
      const progressSection = this.$refs.progressSec;
      const budgetSection = this.$refs.budgetSec;

      if (scrollContainer?.scrollTop === 0) {
        this.activeTab = this.sectionTabs[0];
      }

      if (scrollContainer?.scrollTop > 0) {
        this.activeTab = this.sectionTabs[1];
      }

      if (scrollContainer?.scrollTop >= progressSection.clientHeight) {
        this.activeTab = this.sectionTabs[2];
      }

      if (scrollContainer?.scrollTop >= progressSection.clientHeight + budgetSection.clientHeight) {
        this.activeTab = this.sectionTabs[3];
      }
    },
    scrollTo(element, isBehavior) {
      document.querySelector(element).scrollIntoView({
        behavior: isBehavior ? 'smooth' : 'auto',
      });
    },
    toggleTab(tab, isBehavior = true) {
      userExtAnalytics.log('report_tab_change', {
        tab: tab.name || '',
      });
      const sectionId = `#sec_${tab.id}`;

      this.scrollTo(sectionId, isBehavior);
    },
    handleIntersect(entries) {
      // const intersecting_element = entries[0];

      // if (intersecting_element.isIntersecting) {
      //   const id = intersecting_element.target.id;
      //   const index = Number(id.split('_')[1]);

      //   this.activeTab = this.sectionTabs[index];
      // }
    },
    // selectReport(type) {
    //   routerHelper.changeRoute(`/reports/${type}`);
    // },
    createReport(data) {
      this.routerName = data?.routerName;
      this.reportTitle = data?.title || '';

      if (data?.type === 'costslist') {
        this.$router.push({
          name: this.routerName,
          params: { type: data?.type },
          query: { userId: user.id },
        });

        return;
      }

      if (data?.type) {
        this.isShowForm = true;
        this.chartType = data.type;

        return;
      }

      this.isShowForm = !this.isShowForm;
      this.chartType = null;
    },
    onClickFeedback() {
      this.$store.dispatch('headerView/selectMissedFeatures');
    },
  },
};
</script>

<style module src='./less/reportsMain.less'></style>
